import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Divider,
  Card,
  CardContent,
  CardActions,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  Chip,
  InputAdornment,
  Slider,
  Stack
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  History as HistoryIcon,
  AddCard as AddCardIcon,
  Phone as PhoneIcon,
  PhoneIphone as PhoneIphoneIcon,
  Star as StarIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`billing-tabpanel-${index}`}
      aria-labelledby={`billing-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const Billing = () => {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [balance, setBalance] = useState(0);
  const [formattedBalance, setFormattedBalance] = useState('$0.00');
  const [isUnlimited, setIsUnlimited] = useState(false);
  const [loading, setLoading] = useState(true);
  const [callHistory, setCallHistory] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [topupAmount, setTopupAmount] = useState(50);
  const [customAmount, setCustomAmount] = useState('');
  const [error, setError] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);
  const navigate = useNavigate();

  const fetchBalance = async () => {
    try {
      setLoading(true);
      // Get token from localStorage
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.error('No token available for balance fetch');
        setError('Authentication required');
        return;
      }
      
      console.log('Using token for balance fetch:', token.substring(0, 10) + '...');
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/payment/get-balance`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch balance');
      }
      
      const data = await response.json();
      setBalance(data.balance);
      setFormattedBalance(data.formatted_balance);
      setIsUnlimited(data.is_unlimited || false);
    } catch (err) {
      console.error('Error fetching balance:', err);
      setError('Failed to load balance');
    } finally {
      setLoading(false);
    }
  };

  const fetchCallHistory = async () => {
    try {
      setHistoryLoading(true);
      // Get token from localStorage
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.error('No token available for call history');
        setError('Authentication required');
        return;
      }
      
      console.log('Using token for call history:', token.substring(0, 10) + '...');
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/calls/history?page=${page + 1}&per_page=${rowsPerPage}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch call history');
      }
      
      const data = await response.json();
      setCallHistory(data);
    } catch (err) {
      console.error('Error fetching call history:', err);
      setError('Failed to load call history');
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    // Check if returning from payment flow (from success or cancel pages)
    const isReturningFromPayment = document.referrer.includes('/billing/success') || 
                                  document.referrer.includes('/billing/cancel');
    
    // If returning from payment, add a small delay to allow webhook processing
    if (isReturningFromPayment) {
      // Show loading while we wait
      setLoading(true);
      
      // Small delay to allow webhook to process payment
      const timer = setTimeout(() => {
        fetchBalance();
        fetchCallHistory();
      }, 1500);
      
      return () => clearTimeout(timer);
    } else {
      // Normal case - just fetch data
      fetchBalance();
      fetchCallHistory();
    }
  }, [user]);

  useEffect(() => {
    fetchCallHistory();
  }, [page, rowsPerPage]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTopupAmountChange = (event, newValue) => {
    setTopupAmount(newValue);
    setCustomAmount('');
  };

  const handleCustomAmountChange = (event) => {
    const value = event.target.value;
    if (value === '' || /^\d+(\.\d{0,2})?$/.test(value)) {
      setCustomAmount(value);
      setTopupAmount(0); // Reset slider when custom amount is entered
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setProcessingPayment(true);

    const amount = customAmount ? parseFloat(customAmount) : topupAmount;

    if (amount < 10) {
      setError('Minimum amount is $10');
      setProcessingPayment(false);
      return;
    }

    // For testing - decide whether to use mock payment or real payment
    const useMockPayment = false; // Toggle this for testing

    if (useMockPayment) {
      // Use mock payment flow
      setProcessingPayment(false);
      navigate('/mock-payment', { state: { amount } });
      return;
    }

    try {
      // Get token from localStorage
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.error('No token available for payment processing');
        setError('Authentication required');
        setProcessingPayment(false);
        return;
      }
      
      console.log('Using token for payment:', token.substring(0, 10) + '...');
      console.log('Sending payment request to:', `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/payment/create-checkout-session`);
      console.log('Payment amount:', amount);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/payment/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          amount: amount,
          product_id: process.env.REACT_APP_STRIPE_PRODUCT_ID || 'prod_RljALXohCkjJLa'
        })
      });

      console.log('Payment response status:', response.status);
      
      const data = await response.json().catch(err => {
        console.error('Error parsing response:', err);
        throw new Error('Failed to parse response');
      });
      
      console.log('Payment response data:', data);
      
      if (!response.ok) throw new Error(data.error || `Failed with status: ${response.status}`);
      window.location.href = data.url;
    } catch (error) {
      console.error('Payment error:', error);
      setError(error.message || 'Failed to process payment');
    } finally {
      setProcessingPayment(false);
    }
  };

  return (
    <Box sx={{ py: 3, px: { xs: 2, md: 3 } }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Billing & Balance
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card elevation={2} sx={{ 
            position: 'relative',
            bgcolor: isUnlimited ? 'rgba(255, 215, 0, 0.05)' : 'inherit',
            border: isUnlimited ? '1px solid rgba(255, 215, 0, 0.3)' : 'inherit'
          }}>
            {isUnlimited && (
              <Box sx={{ 
                position: 'absolute', 
                top: 0, 
                right: 0, 
                backgroundColor: 'warning.main', 
                color: 'warning.contrastText',
                px: 1.5,
                py: 0.5,
                borderBottomLeftRadius: 8
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <StarIcon fontSize="small" sx={{ mr: 0.5 }} />
                  <Typography variant="caption" fontWeight="bold">ADMIN</Typography>
                </Box>
              </Box>
            )}
            <CardContent>
              <Typography variant="h6" color={isUnlimited ? 'warning.main' : 'primary'} gutterBottom>
                <AccountBalanceIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Current Balance
              </Typography>
              
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Typography 
                  variant="h3" 
                  sx={{ 
                    fontWeight: 'bold', 
                    my: 2,
                    color: isUnlimited ? 'warning.main' : 'inherit'
                  }}
                >
                  {formattedBalance}
                </Typography>
              )}
              
              <Typography variant="body2" color="text.secondary">
                {isUnlimited 
                  ? "As an admin, you have unlimited balance and can use all services without restrictions."
                  : "Your balance is used for all services including calls and phone number purchases."}
              </Typography>
            </CardContent>
            {!isUnlimited && (
              <CardActions>
                <Button 
                  variant="outlined" 
                  onClick={() => setTabValue(1)} 
                  fullWidth
                >
                  Top Up Balance
                </Button>
              </CardActions>
            )}
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper elevation={2} sx={{ mb: 3 }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
            >
              <Tab icon={<HistoryIcon />} label="Usage History" />
              {!isUnlimited && <Tab icon={<AddCardIcon />} label="Top Up Balance" />}
            </Tabs>

            <TabPanel value={tabValue} index={0}>
              {historyLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                  <CircularProgress />
                </Box>
              ) : callHistory.calls && callHistory.calls.length > 0 ? (
                <>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Duration</TableCell>
                          <TableCell>Cost</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {callHistory.calls.map((call) => (
                          <TableRow key={call.id}>
                            <TableCell>
                              {format(new Date(call.datetime), 'MMM dd, yyyy HH:mm')}
                            </TableCell>
                            <TableCell>
                              <Chip 
                                icon={call.direction === 'outbound' ? <PhoneIcon fontSize="small" /> : <PhoneIphoneIcon fontSize="small" />}
                                label={call.direction.charAt(0).toUpperCase() + call.direction.slice(1)} 
                                color={call.direction === 'outbound' ? 'primary' : 'default'}
                                variant="outlined"
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              {call.duration_formatted || 
                               (call.duration ? `${Math.floor(call.duration / 60)}m ${call.duration % 60}s` : 'N/A')}
                            </TableCell>
                            <TableCell>
                              {isUnlimited ? 'N/A' : 
                               (call.cost_formatted || 
                                (call.cost ? `$${parseFloat(call.cost).toFixed(2)}` : '$0.00'))}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={callHistory.pagination?.total_items || 0}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25]}
                  />
                </>
              ) : (
                <Typography variant="body1" sx={{ textAlign: 'center', my: 3 }}>
                  No usage history found
                </Typography>
              )}
            </TabPanel>

            {!isUnlimited && (
              <TabPanel value={tabValue} index={1}>
                <Box component="form" onSubmit={handleSubmit} noValidate>
                  <Typography variant="h6" gutterBottom>
                    Top up your balance
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Typography gutterBottom>
                        Select an amount:
                      </Typography>
                      <Slider
                        value={topupAmount}
                        onChange={handleTopupAmountChange}
                        aria-labelledby="topup-amount-slider"
                        valueLabelDisplay="auto"
                        step={10}
                        marks={[
                          { value: 10, label: '$10' },
                          { value: 50, label: '$50' },
                          { value: 100, label: '$100' },
                          { value: 200, label: '$200' }
                        ]}
                        min={10}
                        max={200}
                        disabled={customAmount !== ''}
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Divider>
                        <Chip label="OR" />
                      </Divider>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Custom Amount"
                        variant="outlined"
                        value={customAmount}
                        onChange={handleCustomAmountChange}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        helperText="Minimum $10"
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        size="large"
                        fullWidth
                        disabled={processingPayment || (!topupAmount && !customAmount)}
                        sx={{ height: '100%' }}
                      >
                        {processingPayment ? <CircularProgress size={24} /> : `Pay $${customAmount || topupAmount}`}
                      </Button>
                    </Grid>
                  </Grid>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Your payment will be processed securely via Stripe. The amount will be added to your balance immediately after payment.
                  </Typography>
                </Box>
              </TabPanel>
            )}
          </Paper>
          
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Pricing
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" color="primary" gutterBottom>
                      Inbound Calls
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                      $0.15
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Per minute
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" color="primary" gutterBottom>
                      Outbound Calls
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                      $0.15
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Per minute
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" color="primary" gutterBottom>
                      Phone Numbers
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Twilio Rate
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      One-time + monthly fee
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Billing; 